import { InventoryGrouping } from "models/InventoryModel";

/** How the forecasting table orders its products. A per-person choice, kept in this browser. */
export const PRODUCT_SORT = {
  SALES_VOLUME: "salesVolume",
  PRODUCT_NUMBER: "productNumber",
  CUSTOM: "custom",
} as const;
export type ProductSort = typeof PRODUCT_SORT[keyof typeof PRODUCT_SORT];

/** Where a moved product lands relative to the one it was dropped on */
export const MOVE_POSITION = { BEFORE: "before", AFTER: "after" } as const;
export type MovePosition = typeof MOVE_POSITION[keyof typeof MOVE_POSITION];

/** One person's arrangement of the table: sort, pinned products (top first), hand-made order. Keys are grouping keys. */
export type ProductArrangement = {
  sort: ProductSort;
  pinnedKeys: string[];
  customOrder: string[];
};

const STORAGE_KEY = "inventory-forecasting:product-arrangement";
export const DEFAULT_ARRANGEMENT: ProductArrangement = { sort: PRODUCT_SORT.SALES_VOLUME, pinnedKeys: [], customOrder: [] };

const isStringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every(item => typeof item === "string");

export function loadProductArrangement(): ProductArrangement {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (saved && (Object.values(PRODUCT_SORT) as string[]).includes(saved.sort) && isStringArray(saved.pinnedKeys) && isStringArray(saved.customOrder)) {
      return { sort: saved.sort, pinnedKeys: saved.pinnedKeys, customOrder: saved.customOrder };
    }
  } catch { /* storage blocked or unreadable: fall through to the default */ }
  return DEFAULT_ARRANGEMENT;
}

export const isDefaultArrangement = (arrangement: ProductArrangement): boolean =>
  arrangement.sort === DEFAULT_ARRANGEMENT.sort && arrangement.pinnedKeys.length === 0 && arrangement.customOrder.length === 0;

export function saveProductArrangement(arrangement: ProductArrangement): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(arrangement)); } catch { /* ignore quota */ }
}

/**
 * Danican numbers one product's variants in size order (SBL-55031 Twin … SBL-55037 Cal King),
 * so a natural sort on the number lines a product's sizes up smallest to largest, colour by
 * colour. `groupings` arrives in sales-volume order; the sort is stable, so products with no
 * number keep that order at the bottom.
 */
export function sortByProductNumber(groupings: InventoryGrouping[]): InventoryGrouping[] {
  return [...groupings].sort((a, b) => {
    if (!a.manufacturerSku || !b.manufacturerSku) {
      return Number(!a.manufacturerSku) - Number(!b.manufacturerSku);
    }
    return a.manufacturerSku.localeCompare(b.manufacturerSku, undefined, { numeric: true, sensitivity: "base" })
      || a.country.localeCompare(b.country);
  });
}

/** The hand-made order. A product it has never seen (new since the order was made) follows, in the order it arrived. */
export function sortByCustomOrder(groupings: InventoryGrouping[], customOrder: string[]): InventoryGrouping[] {
  const rank = new Map(customOrder.map((key, index) => [key, index]));
  return [...groupings].sort((a, b) => (rank.get(a.key) ?? customOrder.length) - (rank.get(b.key) ?? customOrder.length));
}

export function sortGroupings(groupings: InventoryGrouping[], arrangement: ProductArrangement): InventoryGrouping[] {
  if (arrangement.sort === PRODUCT_SORT.PRODUCT_NUMBER) return sortByProductNumber(groupings);
  if (arrangement.sort === PRODUCT_SORT.CUSTOM) return sortByCustomOrder(groupings, arrangement.customOrder);
  return groupings;
}

/** `keys` with `movedKey` taken out and put back right before or after `targetKey`. Unchanged if either is missing. */
export function moveKey(keys: string[], movedKey: string, targetKey: string, position: MovePosition): string[] {
  if (movedKey === targetKey || !keys.includes(movedKey) || !keys.includes(targetKey)) return keys;
  const withoutMoved = keys.filter(key => key !== movedKey);
  const targetIndex = withoutMoved.indexOf(targetKey);
  withoutMoved.splice(position === MOVE_POSITION.BEFORE ? targetIndex : targetIndex + 1, 0, movedKey);
  return withoutMoved;
}
